import { cellText, valueOf } from './objectEdit.js'
import { place } from './objectLayout.js'

/**
 * RED-317 PR 3d-B — the live preview of a retyped Excel object: the
 * server's SVG of the object's EMF preview (EmfToSvg, every text run a
 * `<text data-r>`) with each changed cell's runs replaced the way the
 * compiler replaces them in the regenerated EMF (ExcelObjectWriter): one
 * text at the place objectLayout.js gives, clipped to the cell; a cleared
 * cell's runs go; a cell that was empty gets a text of its style's font.
 * Patches a fresh clone of the base SVG, so any override (or none) can be
 * drawn at any time; touches only the edited cells.
 */

const SVG_NS = 'http://www.w3.org/2000/svg'

function attributes(style) {
  const out = []
  for (const match of String(style ?? '').matchAll(/([a-z-]+)="([^"]*)"/g)) out.push([match[1], match[2]])
  return out
}

function clipFor(svg, prefix, ref, cell) {
  const id = `${prefix}-${ref}`
  if (!svg.querySelector(`[id="${id}"]`)) {
    let defs = svg.querySelector(':scope > defs')
    if (!defs) {
      defs = document.createElementNS(SVG_NS, 'defs')
      svg.insertBefore(defs, svg.firstChild)
    }
    const [x0, y0, x1, y1] = cell.box
    const clip = document.createElementNS(SVG_NS, 'clipPath')
    clip.setAttribute('id', id)
    const rect = document.createElementNS(SVG_NS, 'rect')
    // ExcelObjectWriter's ETO_CLIPPED rectangle: inside the gridlines.
    rect.setAttribute('x', String(x0 + 1))
    rect.setAttribute('y', String(y0 + 1))
    rect.setAttribute('width', String(Math.max(0, x1 - x0 - 1)))
    rect.setAttribute('height', String(Math.max(0, y1 - y0 - 1)))
    clip.appendChild(rect)
    defs.appendChild(clip)
  }
  return `url(#${id})`
}

/**
 * Apply `edit` to `svg` (a clone of the object's base preview) in place.
 * `prefix` keeps the clip ids of this picture apart from others on the page.
 * Returns the refs drawn.
 */
export function patchPreview(svg, model, fonts, edit, prefix = 'oe') {
  const drawn = []
  for (const ref of Object.keys(edit?.cells ?? {})) {
    const cell = model.cells[ref]
    if (!cell || cell.readonly) continue
    const value = valueOf(model, edit, ref)
    const text = cellText(model, edit, ref)
    const font = model.fonts[cell.font]
    const elements = cell.runs.map((run) => svg.querySelector(`text[data-r="${run.r}"]`)).filter(Boolean)
    const placed = text !== '' && font ? place(fonts, cell, [{ text, font }], model.pads, typeof value === 'number') : null
    if (!placed) {
      for (const element of elements) element.remove()
      drawn.push(ref)
      continue
    }
    let target = elements[0] ?? null
    for (const element of elements.slice(1)) element.remove()
    if (!target) {
      const group = document.createElementNS(SVG_NS, 'g')
      for (const [name, value] of attributes(font.style)) group.setAttribute(name, value)
      target = document.createElementNS(SVG_NS, 'text')
      target.setAttribute('y', String(cell.y + font.ascent))
      group.appendChild(target)
      svg.appendChild(group)
    }
    target.setAttribute('x', String(placed[0].x))
    target.setAttribute('clip-path', clipFor(svg, prefix, ref, cell))
    target.setAttribute('data-ref', ref)
    target.textContent = text
    drawn.push(ref)
  }
  return drawn
}
